<template>
    <div>
        <RouterView />
        <div class="print-only">
            <SettingsPrintVersion />
        </div>
    </div>
</template>

<script>
import SettingsPrintVersion from './settings/SettingsPrintVersion.vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import { secondsToMMSS, ifIsNotCanceled } from './common/helpers';
import {
    resourceAssignmentsConf,
    resourceTargetTime,
    resourceLogSettings,
    resourceDefaultRecipients,
} from '../api/resources';
import { getCancelToken } from '../api';

export default {
    components: { SettingsPrintVersion },

    data() {
        return {
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState({
            assignmentsConf: state => state.assignmentsConf,
            autoLogout: state => state.mariTimeout,
            timeFormat: state => state.timeFormat.date + state.timeFormat.separator + state.timeFormat.time,
            contactInfo: state => state.navigation.contactInfo,
            organizationName: state => state.navigation.organizationName,
            tabVisibility: state => state.user.tabVisibility,
            calls: state => state.targetTimeCalls,
            settings: state => state.logSettings,
            defaultRecipients: state => state.defaultRecipients,
        }),
    },

    created() {
        this.isLoaded(false);
        let dataToLoad = [];
        if (Object.keys(this.assignmentsConf).length === 0) {
            dataToLoad.push(this.loadAssignmentsConf());
        }
        if (this.calls.length === 0) {
            dataToLoad.push(this.loadTargetTime());
        }
        if (this.settings.length === 0) {
            dataToLoad.push(this.loadLogSettings());
        }
        dataToLoad.push(this.loadDefaultRecipients());
        Promise.all(dataToLoad)
            .then(() => {
                this.isLoaded(true);
            })
            .catch(ifIsNotCanceled());
    },

    methods: {
        ...mapActions({
            setAssignmentsSettings: 'setAssignmentsSettings',
            setTargetTimeSettings: 'setTargetTimeSettings',
            setLogSettings: 'setLogSettings',
            setDefaultRecipients: 'setDefaultRecipients',
        }),
        ...mapMutations({
            isLoaded: 'SET_LOADED',
        }),

        secondsToMMSS,

        /**
         * Loads assignments configuration from database
         */
        loadAssignmentsConf() {
            return resourceAssignmentsConf({
                cancelToken: this.cancelToken.token,
            })
                .then(response => response.data)
                .then(configuration => {
                    this.assignments = {
                        ...configuration,
                        staff_color: configuration.staff_color || 1,
                        resident_color: configuration.resident_color || 2,
                        staff_overload: configuration.staff_overload,
                    };
                })
                .catch(response => {
                    this.assignments = {
                        show_resident_call: true,
                        resident_call_name: 'Room',
                        resident_color: 2,
                        show_staff_call: true,
                        staff_call_name: 'Staff',
                        staff_color: 1,
                        assignments_type: 0,
                        assignments_level: 1,
                        show_catch_net: true,
                        staff_overload: 100,
                    };
                    return response;
                })
                .then(() => this.setAssignmentsSettings(this.assignments));
        },

        loadTargetTime() {
            return resourceTargetTime({ cancelToken: this.cancelToken.token }).then(response => {
                this.targetTimeCalls = response.data;
                this.setTargetTimeSettings(this.targetTimeCalls);
            });
        },
        loadLogSettings() {
            return resourceLogSettings({
                cancelToken: this.cancelToken.token,
            })
                .then(response => response.data)
                .then(info => {
                    this.logSettings = [
                        {
                            name: 'Call logging',
                            id: 'callLogging',
                            value: info.call,
                            disabled: false,
                        },
                        {
                            name: 'Card logging',
                            id: 'cardLogging',
                            value: info.card,
                            disabled: info.call,
                        },
                        {
                            name: 'Message logging',
                            id: 'messageLogging',
                            value: info.message,
                            disabled: info.call,
                        },
                    ];
                    this.setLogSettings(this.logSettings);
                });
        },

        loadDefaultRecipients() {
            return resourceDefaultRecipients({
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.setDefaultRecipients(response.data.emails);
                    response.data.emails.forEach(element => {
                        this.emails = this.emails + element + '\n';
                    });
                })
                .then(() => {
                    return Promise.resolve();
                });
        },
    },
};
</script>
